import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  Space,
  Alert,
  Spin,
  message,
  Modal,
  Select,
  Checkbox,
  Form,
  Divider,
} from 'antd';
import {
  RobotOutlined,
  UserOutlined,
  SendOutlined,
  SaveOutlined,
  SearchOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import { TemplateForm } from '../TemplateManagement/TemplateForm';
import type { KrybotTemplateCreate, KrybotAgent } from '../../../types/krybot';
import styles from './PromptBuilder.module.css';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  rawContent?: string; // Full response content from mapping.raw
  timestamp: Date;
}

/**
 * Extracts a prompt from the LLM response text.
 * Tries multiple strategies to find the most relevant prompt content.
 */
const extractPromptFromResponse = (responseText: string): string | null => {
  if (!responseText || responseText.trim().length === 0) {
    return null;
  }

  // Strategy 1: Look for code blocks (most structured)
  const codeBlockRegex = /```(?:prompt|text|markdown)?\s*([\s\S]*?)```/gi;
  const codeBlockMatches = Array.from(responseText.matchAll(codeBlockRegex));
  if (codeBlockMatches.length > 0) {
    // Use the last code block (most recent/refined)
    const lastBlock = codeBlockMatches[codeBlockMatches.length - 1][1].trim();
    if (lastBlock.length > 20) {
      return lastBlock;
    }
  }

  // Strategy 2: Look for text between prompt markers
  const promptMarkerRegex = /(?:prompt|here'?s?\s+(?:the\s+)?(?:a\s+)?prompt|prompt\s*:)\s*:?\s*([\s\S]*?)(?:\n\n|\n---|$)/i;
  const markerMatch = responseText.match(promptMarkerRegex);
  if (markerMatch && markerMatch[1]) {
    const extracted = markerMatch[1].trim();
    if (extracted.length > 20) {
      return extracted;
    }
  }

  // Strategy 3: Look for substantial text blocks that might be prompts
  // (typically longer paragraphs without too many question marks or conversational markers)
  const lines = responseText.split('\n').filter(line => line.trim().length > 0);
  const substantialBlocks: string[] = [];
  let currentBlock: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    // Skip conversational markers
    if (
      trimmed.startsWith('Here') ||
      trimmed.startsWith('Let') ||
      trimmed.startsWith('I ') ||
      trimmed.startsWith('You ') ||
      trimmed.match(/^(Sure|Okay|Great|Thanks|Thank you)/i) ||
      trimmed.endsWith('?')
    ) {
      // If we have a block, save it
      if (currentBlock.length > 0) {
        const block = currentBlock.join('\n').trim();
        if (block.length > 50 && !block.match(/\?{2,}/)) {
          substantialBlocks.push(block);
        }
        currentBlock = [];
      }
      continue;
    }

    // Add to current block if it looks like prompt content
    if (trimmed.length > 10 && !trimmed.match(/^(Note|Remember|Tip|Warning)/i)) {
      currentBlock.push(trimmed);
    }
  }

  // Add final block if it exists
  if (currentBlock.length > 0) {
    const block = currentBlock.join('\n').trim();
    if (block.length > 50) {
      substantialBlocks.push(block);
    }
  }

  // Use the longest substantial block
  if (substantialBlocks.length > 0) {
    const longest = substantialBlocks.reduce((a, b) => (a.length > b.length ? a : b));
    if (longest.length > 50) {
      return longest;
    }
  }

  // Strategy 4: If response is substantial and doesn't look like conversation, use it
  if (responseText.length > 100) {
    // Check if it's mostly prompt-like (not too many questions, not too conversational)
    const questionCount = (responseText.match(/\?/g) || []).length;
    const conversationalMarkers = (responseText.match(/(?:I |You |We |Let's|Here's|That's)/gi) || []).length;
    
    // If it has few questions and few conversational markers, it might be a prompt
    if (questionCount < responseText.length / 200 && conversationalMarkers < responseText.length / 100) {
      return responseText.trim();
    }
  }

  return null;
};

export const PromptBuilderChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState<string>('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [agents, setAgents] = useState<KrybotAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<KrybotAgent | null>(null);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [saveForm] = Form.useForm();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load agents when component mounts
    const loadAgents = async () => {
      try {
        setLoadingAgents(true);
        const agentList = await KrybotService.listAgents({ is_active: true });
        setAgents(agentList);
      } catch (err: any) {
        message.error(`Failed to load agents: ${err.message}`);
      } finally {
        setLoadingAgents(false);
      }
    };
    loadAgents();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setLoading(true);
    setError(null);

    try {
      // Build messages array for Krybot API
      const apiMessages = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));
      
      // Add the current user message
      apiMessages.push({
        role: 'user',
        content: userInput,
      });

      // Prepare agent context if an agent is selected
      const agentContext = selectedAgent
        ? {
            name: selectedAgent.display_name || selectedAgent.name,
            persona_prompt: selectedAgent.persona_prompt || '',
          }
        : undefined;

      // Call Krybot API via prompt builder endpoint
      const response = await KrybotService.promptBuilderChat({
        messages: apiMessages,
        agent_context: agentContext,
      });

      // Extract message and raw content separately
      const messageContent = response.message || 'I apologize, but I could not generate a response.';
      const rawContent = response.mapping?.raw || response.message;
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: messageContent, // Short message
        rawContent: rawContent, // Full response content
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Extract and update prompt from the response
      const responseText = rawContent || messageContent;
      const extractedPrompt = extractPromptFromResponse(responseText);
      if (extractedPrompt) {
        setCurrentPrompt(extractedPrompt);
      }
    } catch (err: any) {
      // Extract detailed error message
      let errorMessage = 'Failed to process message';
      if (err.response?.data) {
        if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      console.error('Prompt builder error:', err);
      setError(errorMessage);
      message.error(`Failed to process message: ${errorMessage}`);
      
      // Add error message to chat with helpful context
      const errorChatMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I encountered an error: ${errorMessage}\n\nThis could be due to:\n- Krybot API not reachable (check KRYBOT_BASE_URL in backend/.env)\n- Krybot API endpoint not available (/api/chat)\n- Network connectivity issues\n\nPlease check the backend logs for more details.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorChatMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsTemplate = () => {
    if (!currentPrompt.trim()) {
      message.warning('No prompt to save. Continue the conversation to build a prompt.');
      return;
    }
    setShowSaveModal(true);
    // Reset form when opening modal
    saveForm.resetFields();
  };

  const handleLoadAgent = async (agentId: string) => {
    try {
      setLoading(true);
      const agent = await KrybotService.getAgent(agentId);
      setSelectedAgent(agent);
      
      if (agent.persona_prompt) {
        // Load the agent's prompt into the current prompt
        setCurrentPrompt(agent.persona_prompt);
        
        // Add a system message explaining what we're analyzing
        const systemMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `I've loaded the persona prompt for agent "${agent.display_name || agent.name}". Let's analyze how this agent is currently behaving and identify areas for improvement.\n\n**Current Agent Prompt:**\n\`\`\`\n${agent.persona_prompt}\n\`\`\`\n\nWhat specific aspects of this agent's behavior would you like to improve? For example:\n- Response style or tone\n- Task handling capabilities\n- Error handling\n- Output format\n- Context understanding`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, systemMessage]);
        message.success(`Loaded agent: ${agent.display_name || agent.name}`);
      } else {
        message.warning('This agent does not have a persona prompt defined.');
      }
      setShowAgentSelector(false);
    } catch (err: any) {
      message.error(`Failed to load agent: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeAgent = async (agentId: string) => {
    try {
      setLoading(true);
      const agent = await KrybotService.getAgent(agentId);
      setSelectedAgent(agent);
      
      if (agent.persona_prompt) {
        setCurrentPrompt(agent.persona_prompt);
        
        // Add analysis request to chat
        const analysisRequest: Message = {
          id: Date.now().toString(),
          role: 'user',
          content: `Analyze this agent's prompt and suggest improvements:\n\n${agent.persona_prompt}`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, analysisRequest]);
        
        // Call Krybot API for real analysis
        const analysisMessages = [
          {
            role: 'system' as const,
            content: 'You are a prompt engineering expert. Analyze AI agent prompts and provide detailed analysis with specific improvement suggestions.',
          },
          {
            role: 'user' as const,
            content: `Analyze the following AI agent prompt and provide a detailed analysis with specific improvement suggestions.

Agent Name: ${agent.display_name || agent.name}
Agent Type: ${agent.agent_type || 'N/A'}
Agent Description: ${agent.description || 'No description provided'}

Current Agent Prompt:
${agent.persona_prompt}

Please provide:
1. **Strengths**: What the current prompt does well
2. **Areas for Improvement**: Specific weaknesses or gaps
3. **Suggested Improvements**: Concrete, actionable recommendations
4. **Improved Prompt**: An enhanced version of the prompt (if applicable)

Format your response clearly with sections and bullet points.`,
          },
        ];
        
        const response = await KrybotService.promptBuilderChat({
          messages: analysisMessages,
          agent_context: {
            name: agent.display_name || agent.name,
            persona_prompt: agent.persona_prompt || '',
          },
        });
        
        const analysisMessageContent = response.message || 'I apologize, but I could not generate an analysis.';
        const analysisRawContent = response.mapping?.raw || analysisMessageContent;
        
        const analysisResponse: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: analysisMessageContent,
          rawContent: analysisRawContent,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, analysisResponse]);
        
        // Extract and update prompt from the analysis response
        const extractedPrompt = extractPromptFromResponse(analysisRawContent);
        if (extractedPrompt) {
          setCurrentPrompt(extractedPrompt);
        } else if (agent.persona_prompt) {
          // If no prompt extracted but we have the agent's prompt, use that as starting point
          setCurrentPrompt(agent.persona_prompt);
        }
        
        message.success('Analysis complete');
      } else {
        message.warning('This agent does not have a persona prompt to analyze.');
      }
      setShowAgentSelector(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to analyze agent';
      message.error(`Failed to analyze agent: ${errorMessage}`);
      
      // Add error message to chat
      const errorChatMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I encountered an error while analyzing the agent: ${errorMessage}. Please check your Krybot API configuration (KRYBOT_BASE_URL) or try again.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorChatMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrompt = async (values: {
    saveAsTemplate: boolean;
    updateAgent: boolean;
    selectedAgentId?: string;
  }) => {
    try {
      setLoading(true);
      const operations: string[] = [];

      // Save as template if requested
      if (values.saveAsTemplate) {
        // This will be handled by TemplateForm component
        operations.push('template');
      }

      // Update agent persona_prompt if requested
      if (values.updateAgent && values.selectedAgentId) {
        await KrybotService.updateAgent(values.selectedAgentId, {
          persona_prompt: currentPrompt,
        });
        operations.push('agent');
        message.success('Agent persona prompt updated successfully');
      }

      if (operations.length === 0) {
        message.warning('Please select at least one save option');
        return;
      }

      // If saving as template, the TemplateForm will handle it
      // We just need to close the modal after template is saved
      if (values.saveAsTemplate) {
        // TemplateForm's onSave will be called after template is created
        return;
      } else {
        // Only updating agent, close modal
        setShowSaveModal(false);
        setCurrentPrompt('');
        message.success('Prompt saved successfully');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save prompt';
      message.error(`Failed to save prompt: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.promptBuilderChat}>
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>Prompt Builder - Chat with LLM</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<SearchOutlined />}
              onClick={() => setShowAgentSelector(true)}
            >
              Load Agent
            </Button>
            {currentPrompt && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveAsTemplate}
              >
                Save Prompt
              </Button>
            )}
          </Space>
        }
        styles={{
          body: {
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 200px)',
            minHeight: '600px',
            padding: 0,
          },
        }}
      >
        {/* Messages area - scrollable, takes remaining space */}
        <div 
          className={styles.chatMessages} 
          style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            minHeight: 0, // Important for flex scrolling
          }}
        >
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#8c8c8c' }}>
              <RobotOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <Paragraph>
                Start a conversation to build your prompt. Describe what you want the prompt to do,
                and I'll help you structure it.
              </Paragraph>
            </div>
          )}

          <List
            dataSource={messages}
            renderItem={(msg) => (
              <List.Item style={{ border: 'none', padding: '12px 0' }}>
                <Space align="start" style={{ width: '100%' }}>
                  <Avatar
                    icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    style={{
                      backgroundColor: msg.role === 'user' ? '#1890ff' : '#52c41a',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text strong>{msg.role === 'user' ? 'You' : 'Assistant'}</Text>
                    {/* Display short message */}
                    <Paragraph style={{ marginTop: 4, marginBottom: msg.rawContent ? 8 : 0, whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Paragraph>
                    {/* Display full response content separately if available */}
                    {msg.rawContent && msg.rawContent !== msg.content && (
                      <div style={{ 
                        marginTop: 8, 
                        padding: 12, 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: 4,
                        border: '1px solid #d9d9d9'
                      }}>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>
                          Full Response:
                        </Text>
                        <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                          {msg.rawContent}
                        </Paragraph>
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                      {msg.timestamp.toLocaleTimeString()}
                    </Text>
                  </div>
                </Space>
              </List.Item>
            )}
          />

          {loading && (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin />
              <div style={{ marginTop: '8px' }}>Thinking...</div>
            </div>
          )}

          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              closable
              onClose={() => setError(null)}
              style={{ marginTop: 16 }}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Fixed bottom section with agent info, current prompt, and input */}
        <div style={{
          borderTop: '1px solid #f0f0f0',
          padding: '16px',
          backgroundColor: '#fafafa',
          flexShrink: 0,
        }}>
          {selectedAgent && (
            <Alert
              message={`Analyzing Agent: ${selectedAgent.display_name || selectedAgent.name}`}
              description={`Agent Type: ${selectedAgent.agent_type || 'N/A'} | ${selectedAgent.description || 'No description'}`}
              type="info"
              closable
              onClose={() => {
                setSelectedAgent(null);
                setCurrentPrompt('');
              }}
              style={{ marginBottom: 16 }}
            />
          )}

          {currentPrompt && (
            <Card
              title={selectedAgent ? "Agent's Current Prompt" : "Current Prompt"}
              size="small"
              style={{ marginBottom: 16, backgroundColor: '#fff', maxHeight: '150px', overflowY: 'auto' }}
              extra={
                selectedAgent && (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleAnalyzeAgent(selectedAgent.id)}
                  >
                    Analyze & Improve
                  </Button>
                )
              }
            >
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: 12 }}>
                {currentPrompt}
              </pre>
            </Card>
          )}

          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Describe what you want the prompt to do..."
              rows={3}
              disabled={loading}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={loading}
              style={{ height: 'auto' }}
            >
              Send
            </Button>
          </Space.Compact>
        </div>
      </Card>

      {showSaveModal && (
        <Modal
          title="Save Prompt"
          open={showSaveModal}
          onCancel={() => {
            setShowSaveModal(false);
            saveForm.resetFields();
          }}
          footer={null}
          width={900}
        >
          <Form
            form={saveForm}
            layout="vertical"
            onFinish={handleSavePrompt}
            initialValues={{
              saveAsTemplate: true,
              updateAgent: false,
            }}
          >
            <Alert
              message="Save Options"
              description="Choose how you want to save this prompt. You can save as a template, update an agent's persona prompt, or both."
              type="info"
              style={{ marginBottom: 24 }}
            />

            <Form.Item name="saveAsTemplate" valuePropName="checked">
              <Checkbox>Save as Template</Checkbox>
            </Form.Item>

            <Form.Item name="updateAgent" valuePropName="checked">
              <Checkbox>Update Agent Persona Prompt</Checkbox>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.updateAgent !== currentValues.updateAgent
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('updateAgent') ? (
                  <Form.Item
                    name="selectedAgentId"
                    label="Select Agent"
                    rules={[
                      {
                        required: true,
                        message: 'Please select an agent',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Choose an agent to update"
                      loading={loadingAgents}
                      showSearch
                      filterOption={(input, option) => {
                        const searchText = input.toLowerCase();
                        const agent = agents.find((a) => a.id === option?.value);
                        if (!agent) return false;
                        const displayText = `${agent.display_name || agent.name}${agent.agent_type ? ` (${agent.agent_type})` : ''}`;
                        return displayText.toLowerCase().includes(searchText);
                      }}
                    >
                      {agents.map((agent) => (
                        <Option key={agent.id} value={agent.id}>
                          {agent.display_name || agent.name}
                          {agent.agent_type && ` (${agent.agent_type})`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Divider />

            {/* Show template form if saving as template */}
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.saveAsTemplate !== currentValues.saveAsTemplate
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('saveAsTemplate') ? (
                  <div style={{ marginTop: 16 }}>
                    <TemplateForm
                      template={null}
                      initialPromptText={currentPrompt}
                      onSave={async () => {
                        // Check if we also need to update agent
                        const formValues = saveForm.getFieldsValue();
                        if (formValues.updateAgent && formValues.selectedAgentId) {
                          try {
                            await KrybotService.updateAgent(formValues.selectedAgentId, {
                              persona_prompt: currentPrompt,
                            });
                            message.success('Template saved and agent updated successfully');
                          } catch (err: any) {
                            message.error(`Template saved, but failed to update agent: ${err.message}`);
                          }
                        } else {
                          message.success('Template saved successfully');
                        }
                        setShowSaveModal(false);
                        setCurrentPrompt('');
                        saveForm.resetFields();
                      }}
                      onCancel={() => {
                        setShowSaveModal(false);
                        saveForm.resetFields();
                      }}
                    />
                  </div>
                ) : (
                  <Form.Item>
                    <Space>
                      <Button
                        onClick={() => {
                          setShowSaveModal(false);
                          saveForm.resetFields();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Save to Agent
                      </Button>
                    </Space>
                  </Form.Item>
                )
              }
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* Agent Selector Modal */}
      <Modal
        title="Select Agent to Load or Analyze"
        open={showAgentSelector}
        onCancel={() => setShowAgentSelector(false)}
        footer={null}
        width={600}
      >
        <List
          loading={loadingAgents}
          dataSource={agents}
          renderItem={(agent) => (
            <List.Item
              actions={[
                <Button
                  key="load"
                  size="small"
                  onClick={() => handleLoadAgent(agent.id)}
                >
                  Load Prompt
                </Button>,
                <Button
                  key="analyze"
                  type="primary"
                  size="small"
                  icon={<SearchOutlined />}
                  onClick={() => handleAnalyzeAgent(agent.id)}
                >
                  Analyze
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<RobotOutlined />} />}
                title={agent.display_name || agent.name}
                description={
                  <Space direction="vertical" size="small">
                    <Text type="secondary">
                      {agent.description || 'No description'}
                    </Text>
                    <Space>
                      {agent.agent_type && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Type: {agent.agent_type}
                        </Text>
                      )}
                      {agent.persona_prompt ? (
                        <Text type="success" style={{ fontSize: 12 }}>
                          Has prompt
                        </Text>
                      ) : (
                        <Text type="warning" style={{ fontSize: 12 }}>
                          No prompt
                        </Text>
                      )}
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

